import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivationEnd, NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { ReplaySubject, filter, firstValueFrom, takeUntil } from 'rxjs';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { getFeatureFlagObservable } from 'app/core/state';
import { Loggable } from 'app/utils/logging/loggable';
import Social from 'entity/Social';
import SocialRoomSet from 'entity/SocialRoomSet';

import { ScootaversePageService } from '../services/scootaverse-page.service';
import { scootaversePageActions } from '../state';

import { ScootaverseRoutingService } from './../services/scootaverse-routing.service';
import { NavRoute, NavRoutes, ParamsRelations } from './constants';
import { VideoChatService } from 'app/modules/social/services/videochat.service';

@Component({
  selector: 'scootaverse-page',
  templateUrl: './scootaverse-page.component.html',
  styleUrls: ['./scootaverse-page.component.scss'],
  standalone: false,
})
export class ScootaversePageComponent extends Loggable implements OnInit, OnDestroy {
  public isDrawerOpen = false;
  public enableEntryFlows = false;
  private destroyed$ = new ReplaySubject<boolean>(1);
  protected currentRoute = '';
  protected navRoutes = NavRoutes;
  protected subItemParameters = ParamsRelations;
  protected showSideMenu = false;
  protected roomName?: string;
  protected scootaverseName?: string;
  protected social?: Social;
  protected scootaverseId?: string;
  protected newScootaverseName?: string = '';
  protected editingName = false;
  private roomSet?: SocialRoomSet;

  public constructor(
    private store: Store,
    private scootaversePageService: ScootaversePageService,
    private scootaverseRoutingService: ScootaverseRoutingService,
    private router: Router,
    private videoChatService: VideoChatService,
  ) {
    super();
    this.setDisplayName('ScootaversePageComponent');
    this.store.dispatch(scootaversePageActions.INIT());

    getFeatureFlagObservable(store, LaunchDarklyFlags.entry_flows_v1)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((enable) => (this.enableEntryFlows = enable));

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd || event instanceof ActivationEnd),
        takeUntil(this.destroyed$),
      )
      .subscribe(async (event) => {
        if (event instanceof NavigationEnd) {
          this.formatCurrentRoute(event as NavigationEnd);
        }
        if (event instanceof ActivationEnd) {
          if (event.snapshot.paramMap.keys.length > 0) {
            const socialId = event.snapshot.paramMap.get('room-id');
            const scootaverseId = event.snapshot.paramMap.get('id');

            if (scootaverseId) {
              this.scootaverseId = scootaverseId;
              // TODO: Check if this is necessary
              this.scootaversePageService.updateSelectedScootaverse(scootaverseId);
            } else {
              this.scootaverseId = undefined;
              this.scootaverseName = undefined;
            }
            if (socialId) {
              this.social = await firstValueFrom(this.videoChatService.getSocial(socialId, false));
              firstValueFrom(this.videoChatService.getSocial(socialId, false))
                .then((social) => {
                  this.roomName = social.name;
                })
                .catch((err: unknown) => {
                  this.error('Error loading social information: ', err);
                });
            } else {
              this.social = undefined;
              this.roomName = undefined;
            }
          }
        }
      });

    this.scootaversePageService.selectedScootaverse$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((selectedScootaverse) => {
        this.roomSet = selectedScootaverse;
        this.scootaverseName = selectedScootaverse?.name;
      });
  }

  public ngOnInit(): void {
    this.openDrawer();
  }

  public openDrawer(): void {
    this.isDrawerOpen = true;
  }

  public closeDrawer() {
    this.isDrawerOpen = false;
  }

  public drawerEvent(event: boolean): void {
    this.isDrawerOpen = event;
  }

  public ngOnDestroy(): void {
    if (this.isDrawerOpen) {
      this.closeDrawer();
    }
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private checkFlag(flag?: string): boolean {
    switch (flag) {
      //Add on this switch any flag conditions for side menu options
      case 'entry_flows_v1':
        return this.enableEntryFlows;
      default:
        return true;
    }
  }

  public flagIsAvailable(navItem: { flag?: string }): boolean {
    if (navItem.flag) {
      return this.checkFlag(navItem.flag);
    }
    return true;
  }

  public async clickSideMenuRoute(navItem: NavRoute): Promise<void> {
    const parametersToRedirect = { id: this.scootaverseId, 'room-id': this.social?.socialId };
    await this.scootaverseRoutingService
      .navigateWithParameters(navItem.url, navItem.parameters ?? [], parametersToRedirect)
      .then((redirectedUrl) => {
        this.currentRoute = redirectedUrl;
      });
  }

  public showSideMenuOption(item: { isSideMenu: boolean; isVisible: boolean; requireParameters?: string[] }): boolean {
    if (!item.requireParameters) {
      // If page not require any extra parameters to be showed, check only sideMenu and visible conditions
      return item.isSideMenu && item.isVisible;
    }

    let hasParameters = true;
    // If page require any extra parameters check for them
    item.requireParameters.forEach((parameter) => {
      switch (parameter) {
        case 'id':
          if (!this.scootaverseId) {
            hasParameters = false;
          }
          break;
        case 'room-id':
          if (!this.social?.socialId) {
            hasParameters = false;
          }
          break;
      }
    });
    return item.isSideMenu && item.isVisible && hasParameters;
  }

  public checkURLActive(item: NavRoute): boolean {
    return this.scootaverseRoutingService.urlChecker(
      this.scootaverseRoutingService.formatUrlWithoutParams(this.currentRoute),
      item.url,
      item.urlActivationParams || [],
      item.requireParameters,
      { scootaverseId: this.scootaverseId, socialId: this.social?.socialId },
    );
  }

  private formatCurrentRoute(event: NavigationEnd): void {
    this.currentRoute = event.url;
    const mainRoute = this.navRoutes.find((route) =>
      this.scootaverseRoutingService.urlChecker(
        this.scootaverseRoutingService.formatUrlWithoutParams(this.currentRoute),
        route.url,
        route.urlActivationParams || [],
        route.requireParameters || [],
        { scootaverseId: this.scootaverseId, socialId: this.social?.socialId },
      ),
    );

    if (!mainRoute) {
      this.navRoutes.forEach((route) => {
        const subRoute = route.subRoutes?.find((sub) =>
          this.scootaverseRoutingService.urlChecker(
            this.scootaverseRoutingService.formatUrlWithoutParams(this.currentRoute),
            sub.url,
            sub.urlActivationParams || [],
            sub.requireParameters || [],
            { scootaverseId: this.scootaverseId, socialId: this.social?.socialId },
          ),
        );
        if (subRoute) {
          this.showSideMenu = subRoute?.isSideMenu ?? false;
          return;
        }
      });
    } else {
      this.showSideMenu = mainRoute?.isSideMenu ?? false;
    }
  }

  protected startEditing(name?: string) {
    this.newScootaverseName = name;
    this.editingName = true;
  }

  protected async editScootaverseName() {
    this.editingName = false;

    if (!this.roomSet) {
      this.error('Unable to get the SocialRoomSetId');
      return;
    }

    if (!this.newScootaverseName) {
      this.error('Name is required');
      this.newScootaverseName = this.roomSet?.name;
      return;
    }

    this.scootaverseName = this.newScootaverseName;

    await this.scootaversePageService
      .updateSocialRoomSet({
        socialRoomSetId: this.roomSet.socialRoomSetId,
        name: this.newScootaverseName,
      })
      .catch((err) => this.error(err));
  }
}
